import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useNavigate } from 'react-router-dom';
import { getErrorMessage, httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';
import { DEMO_LOGIN_ENABLED } from '@/config/demoLogin';

const PASSWORD_REQUIRED = import.meta.env.VITE_DEMO_SM_PASSWORD_REQUIRED === 'true';

interface ServiceMemberCandidate {
  id: string;
  display_name: string | null;
  email: string;
  rank: string | null;
  unit: string | null;
  service_branch: string | null;
  individual_id: string | null;
}

export const ServiceMemberLoginPage: React.FC = () => {
  const {
    loginAsDemoServiceMember,
    isLoading,
    isDemoServiceMemberSession,
    isAuthenticated,
    hasRole,
    isFrontWindowOnly,
    isServiceMember,
    isAdmin,
  } = useAuth();
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState<ServiceMemberCandidate[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [password, setPassword] = useState('');

  const fetchCandidates = useCallback(async () => {
    setIsFetching(true);
    setFetchError(null);
    try {
      const data = await httpClient.get<ServiceMemberCandidate[]>(
        API_CONFIG.ENDPOINTS.SERVICE_MEMBER_CANDIDATES,
        { skipAuth: true },
      );
      setCandidates(data);
    } catch (err) {
      setFetchError(getErrorMessage(err));
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    void fetchCandidates();
  }, [fetchCandidates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (c) =>
        (c.display_name ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q) ||
        (c.rank ?? '').toLowerCase().includes(q) ||
        (c.unit ?? '').toLowerCase().includes(q) ||
        (c.service_branch ?? '').toLowerCase().includes(q),
    );
  }, [candidates, search]);

  const handleSelect = useCallback(
    async (candidate: ServiceMemberCandidate) => {
      if (PASSWORD_REQUIRED && !password.trim()) {
        setSignInError('Enter the demo password before selecting a person.');
        return;
      }
      setIsSigningIn(true);
      setSignInError(null);
      try {
        await loginAsDemoServiceMember(candidate, PASSWORD_REQUIRED ? password : undefined);
        navigate('/notifications', { replace: true });
      } catch (err) {
        setSignInError(
          getErrorMessage(err) || 'Failed to sign in. Check the password and try again.',
        );
        setIsSigningIn(false);
      }
    },
    [loginAsDemoServiceMember, navigate, password],
  );

  if (!DEMO_LOGIN_ENABLED) return <Navigate to="/login" replace />;
  // Only redirect away if the current session is already a completed demo
  // service-member session — not just any authenticated state.
  // Authenticated regular users should be able to sign in as a service member
  // from this page without being kicked back to the home screen. (#316)
  if (isDemoServiceMemberSession) return <Navigate to="/notifications" replace />;

  if (isLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Allow all operational roles that are permitted to use SM impersonation:
  //   admin, user-only, frontwindow, comptroller.
  // Block: viewer (read-only role, no operational context),
  //        service-member (already in an impersonated session),
  //        finance and hr (no operational impersonation context) (#512).
  // isFrontWindowOnly users have 'user' suppressed by resolveRoles so we must
  // check isFrontWindowOnly explicitly (#455).
  // isAdmin is checked separately because some admin accounts carry only the
  // 'admin' role without 'user' (#1000).
  const isAllowedRole =
    isAuthenticated &&
    !isServiceMember &&
    !hasRole('viewer') &&
    !hasRole('finance') &&
    !hasRole('hr') &&
    (hasRole('user') || isFrontWindowOnly || hasRole('comptroller') || isAdmin);
  if (!isAllowedRole) return <Navigate to="/login" replace />;

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: { xs: 0, sm: 2 },
        py: { xs: 0, sm: 3 },
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 560,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: { xs: '100vh', sm: 'calc(100vh - 48px)' },
          maxHeight: { xs: '100vh', sm: 'calc(100vh - 48px)' },
          borderRadius: { xs: 0, sm: 2 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <IconButton
            onClick={() => navigate('/login')}
            size="small"
            sx={{ mr: 1 }}
            aria-label="Back to login"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" lineHeight={1.2}>
              Service Member Sign In
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {candidates.length > 0
                ? `${filtered.length} of ${candidates.length} persons`
                : 'Select a person from Person Management'}
            </Typography>
          </Box>
        </Box>

        {/* Search + optional password */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          {PASSWORD_REQUIRED && (
            <TextField
              fullWidth
              size="small"
              type="password"
              placeholder="Demo password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 1 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, rank, unit or branch…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus={!PASSWORD_REQUIRED}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        {/* Person list — flex: 1 fills all remaining height so all persons are reachable via scroll */}
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {isFetching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {fetchError && !isFetching && (
            <Box sx={{ p: 2 }}>
              <Alert
                severity="error"
                action={
                  <IconButton
                    size="small"
                    aria-label="Retry"
                    onClick={() => {
                      void fetchCandidates();
                    }}
                  >
                    {'\u21BA'}
                  </IconButton>
                }
              >
                {fetchError}
              </Alert>
            </Box>
          )}

          {!isFetching && !fetchError && filtered.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 6, px: 2 }}>
              {candidates.length === 0
                ? 'No persons found in Person Management.'
                : 'No matching persons found.'}
            </Typography>
          )}

          {!isFetching && !fetchError && filtered.length > 0 && (
            <List dense disablePadding>
              {filtered.map((candidate, idx) => {
                const secondary = [candidate.rank, candidate.unit, candidate.service_branch]
                  .filter(Boolean)
                  .join(' \u00B7 ');
                return (
                  <React.Fragment key={candidate.id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          void handleSelect(candidate);
                        }}
                        disabled={isSigningIn}
                      >
                        <ListItemText
                          primary={candidate.display_name ?? candidate.email}
                          secondary={secondary || candidate.email}
                        />
                        {isSigningIn && (
                          <CircularProgress size={16} sx={{ ml: 1, flexShrink: 0 }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* Footer: sign-in error */}
        {signInError && (
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Alert severity="error">{signInError}</Alert>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
